import { z } from "zod";
import { questionIdSchema } from "./ids";
import { isBase62SortKey, keyBetween } from "./sortKey";
import {
  CONTENT_SCHEMA_VERSION,
  READABLE_SCHEMA_VERSIONS,
  formContentSchema,
  type FormContent,
} from "./types";

export const MAX_FORM_JSON_LENGTH = 1_000_000;
const envelopeSchema = z
  .object({
    format: z.literal("sfc-form"),
    schemaVersion: z.number().int(),
    content: z.unknown(),
  })
  .strict();

/** A transfer contains authored content only, never environment-owned records. */
export function exportFormJson(content: FormContent): string {
  const json = JSON.stringify(
    { format: "sfc-form", schemaVersion: CONTENT_SCHEMA_VERSION, content },
    null,
    2,
  );
  // Present only snapshots that this same build can import. This applies the
  // size and draft-storage constraints at both ends of the transfer.
  importFormJson(json);
  return json;
}

// Zod strips unknown keys by default. At this boundary that would silently
// lose settings copied from a branch with features this build doesn't know.
function assertTransferableFields(
  raw: unknown,
  parsed: unknown,
  path: string,
): void {
  // With Unicode mode, valid surrogate pairs are single code points outside
  // this range. Lone surrogates and NUL cannot be stored in PostgreSQL JSONB.
  if (
    typeof raw === "string" &&
    (raw.includes("\0") || /[\uD800-\uDFFF]/u.test(raw))
  ) {
    throw new Error(
      `Invalid text at ${path}: NUL characters and unpaired Unicode surrogates are not supported.`,
    );
  }
  if (
    raw === null ||
    typeof raw !== "object" ||
    parsed === null ||
    typeof parsed !== "object"
  )
    return;
  for (const key of Object.keys(raw)) {
    if (!Object.hasOwn(parsed, key))
      throw new Error(
        `Unsupported field at ${path}.${key}. Import into a build that supports this form.`,
      );
    assertTransferableFields(
      (raw as Record<string, unknown>)[key],
      (parsed as Record<string, unknown>)[key],
      `${path}.${key}`,
    );
  }
}

function assertUnique(keys: string[], label: string): void {
  if (keys.some((key) => !key) || new Set(keys).size !== keys.length) {
    throw new Error(`${label} must be nonempty and unique.`);
  }
}

/** The editor must be able to insert into every gap after import. */
function assertEditableOrder(keys: string[], label: string): void {
  const ordered = [...new Set(keys)].sort();
  try {
    if (ordered.some((key) => !isBase62SortKey(key)))
      throw new Error("Invalid ordering key");
    for (let index = 0; index <= ordered.length; index++) {
      const prev = ordered[index - 1] ?? null;
      const next = ordered[index] ?? null;
      const between = keyBetween(prev, next, { unique: false });
      if (
        (prev !== null && between <= prev) ||
        (next !== null && between >= next)
      ) {
        throw new Error("No room between ordering keys");
      }
    }
  } catch {
    throw new Error(
      `Invalid ordering in ${label}. Export the form again from the builder.`,
    );
  }
}

/** Incomplete drafts may travel too. Publishing remains the semantic gate. */
export function importFormJson(json: string): FormContent {
  if (json.length > MAX_FORM_JSON_LENGTH)
    throw new Error(
      "This form JSON is too large to transfer (maximum 1 million characters).",
    );
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new Error("This isn't valid JSON. Paste the complete form export.");
  }
  const envelope = envelopeSchema.safeParse(raw);
  if (!envelope.success)
    throw new Error(
      "This isn't an SFC form export. Copy JSON from the form editor and paste it here.",
    );
  if (
    !(READABLE_SCHEMA_VERSIONS as readonly number[]).includes(
      envelope.data.schemaVersion,
    )
  ) {
    throw new Error(
      `This form uses schema version ${envelope.data.schemaVersion}, which this build doesn't support. Import into a newer build.`,
    );
  }
  const parsed = formContentSchema.safeParse(envelope.data.content);
  if (!parsed.success) {
    const issue = parsed.error.issues[0]!;
    throw new Error(
      `Invalid form at content.${issue.path.join(".")}: ${issue.message}`,
    );
  }
  const content = parsed.data;
  assertTransferableFields(envelope.data.content, content, "content");
  // The collaborative document stores these lists as maps. Duplicate keys
  // would overwrite authored content during seeding, so reject before writing.
  assertUnique(
    content.sections.map((section) => section.id),
    "Section IDs",
  );
  assertUnique(
    content.questions.map((question) => question.id),
    "Question IDs",
  );
  assertEditableOrder(
    content.sections.map((section) => section.pos),
    "sections",
  );
  assertUnique(
    (content.groups ?? []).map((group) => group.id),
    "Group IDs",
  );
  for (const group of content.groups ?? []) {
    if (
      !questionIdSchema.safeParse(group.id).success ||
      content.questions.some((q) => q.id === group.id)
    )
      throw new Error(
        "Group identities must be valid and distinct from question identities.",
      );
  }
  const questionPositions = new Map<string, string[]>();
  for (const question of content.questions) {
    const identity = questionIdSchema.safeParse(question.id);
    if (!identity.success) {
      throw new Error(
        `Invalid question ID: ${identity.error.issues[0]!.message}`,
      );
    }
    const positions = questionPositions.get(question.sectionId) ?? [];
    positions.push(question.pos);
    questionPositions.set(question.sectionId, positions);
    for (const list of ["options", "rows", "fields"] as const) {
      assertEditableOrder(
        (question.config?.[list] ?? []).map((item) => item.pos),
        `${question.id} ${list}`,
      );
    }
    assertUnique(
      (question.config?.options ?? []).map((option) => option.id),
      `Option IDs in ${question.id}`,
    );
    assertUnique(
      (question.config?.rows ?? []).map((row) => row.id),
      `Row IDs in ${question.id}`,
    );
    assertUnique(
      (question.config?.fields ?? []).map((field) => field.id),
      `Field IDs in ${question.id}`,
    );
    for (const field of question.config?.fields ?? []) {
      if (!questionIdSchema.safeParse(field.id).success)
        throw new Error(`Invalid repeatable field ID in ${question.id}.`);
    }
    assertUnique(
      (question.validation ?? []).map((rule) => rule.kind),
      `Validation rule kinds in ${question.id}`,
    );
  }
  for (const [sectionId, positions] of questionPositions) {
    assertEditableOrder(positions, `questions in ${sectionId}`);
  }
  return content;
}
